// src/components/Agency/CreatorBioTab.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../../../API';
import AlertDialog from '../../../Components/AlertDialog';
import useAlertDialog from '../../../Components/useAlertDialog';
import { BioPageContainer } from '../../../CreatorUser/CreatorPages/Dashboard/Bio/previewhelper';
import UploadFileDialogPFP from '../../../CreatorUser/onboardupload';
import { styled } from '@mui/material/styles';
import ColorPickerGrid from '../../../CreatorUser/CreatorPages/Dashboard/Bio/colorpickergrid';
import { useCreator } from '../../../Hooks/agencySelectedCreatorContext';
import { useParams } from 'react-router-dom'

// Styled components
const ActionCard = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  cursor: 'pointer',
  boxShadow: theme.shadows[3],
  '&:hover .itemOverlay': { opacity: 1 },
}));

const ItemMedia = styled('div')({
  width: '100%',
  height: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Overlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0,0,0,0.4)',
  backdropFilter: 'blur(6px)',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  textAlign: 'center',
  padding: theme.spacing(2),
  color: '#fff',
}));

export default function CreatorBioTab() {
  // 1) ALL hooks at the top, before any returns:
  const queryClient = useQueryClient();
  const { creatorId } = useParams();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down('sm'));


  // fetch pages
  const {
    data: pages = [],
    isLoading: isPagesLoading,
    error: pagesError,
  } = useQuery(
    ['agencyBioPages', creatorId],
       () => API.bio.listAgency(creatorId),
       { enabled: !!creatorId }
  );
  

  // local state
  const [isNewPage, setIsNewPage] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(-1);
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [qrEnabled, setQrEnabled] = useState(false);
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [themeObj, setThemeObj] = useState({
    backgroundColor: '',
    foregroundColor: '',
  });
  const [items, setItems] = useState([]);
  const [isBgPickerOpen, setIsBgPickerOpen] = useState(false);
  const [isFgPickerOpen, setIsFgPickerOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // mutations
  const createMutation = useMutation(
    (newPage) =>
      API.bio.createAgency({ creator_username: creatorId, ...newPage }),
    {
      onSuccess: () => {
        openDialog('Success', 'Bio page created!', () => {
          closeDialog();
          queryClient.invalidateQueries([
            'agencyBioPages',
            creatorId,
          ]);
        });
      },
      onError: (err) => {
        openDialog('Error', err?.response?.data?.error || err.message, closeDialog);
      },
    }
  );

  const updateMutation = useMutation(
    ({ oldSlug, updated }) =>
      API.bio.updateAgency(oldSlug, {
        creator_username: creatorId,
        ...updated,
      }),
    {
      onSuccess: () => {
        openDialog('Success', 'Bio page updated!', () => {
          closeDialog();
          queryClient.invalidateQueries([
            'agencyBioPages',
            creatorId,
          ]);
        });
      },
      onError: (err) => {
        openDialog('Error', err?.response?.data?.error || err.message, closeDialog);
      },
    }
  );

  // 2) Early returns after hooks:
  if (!creatorId) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isPagesLoading) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pagesError) {
    return <Typography color="error">Error loading bio pages.</Typography>;
  }

  // 3) Event handlers
  function handleSelectPage(index) {
    if (index === -1) {
      setIsNewPage(true);
      setSelectedPageIndex(-1);
      setSlug(creatorId);
      setTitle('');
      setDescription('');
      setIsPublic(true);
      setQrEnabled(false);
      setHeaderMediaUrl('');
      setThemeObj({ backgroundColor: '', foregroundColor: '' });
      setItems([]);
    } else {
      setIsNewPage(false);
      setSelectedPageIndex(index);
      const page = pages[index];
      setSlug(page.slug);
      setTitle(page.title || '');
      setDescription(page.description || '');
      setIsPublic(page.is_public);
      setQrEnabled(page.qr_enabled);
      setHeaderMediaUrl(page.header_media_url || '');
      setThemeObj(page.theme || {});
      setItems(page.items || []);
    }
  }

  function handleAddItem() {
    setItems((prev) => [
      ...prev,
      {
        component_type: 'LINK',
        label: 'New Link',
        url: '',
        description: '',
        media_url: '',
        size: 'MEDIUM',
        is_enabled: true,
      },
    ]);
  }

  function handleRemoveItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleUpdateItemField(idx, field, value) {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  }

  function handleSave() {
    if (!slug) {
      openDialog('Validation Error', 'A slug is required.', closeDialog);
      return;
    }
    const payload = {
      slug,
      title,
      description,
      is_public: isPublic,
      qr_enabled: qrEnabled,
      theme: themeObj,
      header_media_url: headerMediaUrl,
      items: items.map((it, idx) => ({ ...it, order_index: idx })),
    };
    if (isNewPage) {
      createMutation.mutate(payload);
    } else {
      const oldSlug = pages[selectedPageIndex].slug;
      updateMutation.mutate({ oldSlug, updated: payload });
    }
  }

  function handleOpenUploadDialog(target) {
    setUploadTarget(target);
    setIsUploadDialogOpen(true);
  }

  function handleCloseUploadDialog(url) {
    setIsUploadDialogOpen(false);
    if (!url) return;
    if (uploadTarget === 'header') {
      setHeaderMediaUrl(url);
    } else if (typeof uploadTarget === 'number') {
      handleUpdateItemField(uploadTarget, 'media_url', url);
    }
  }

  function onDragEnd(result) {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(source.index, 1);
      updated.splice(destination.index, 0, moved);
      return updated;
    });
  }

  function renderPreviewItem(item, idx) {
    const isImage = /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.media_url);
    return (
      <ActionCard key={idx} onClick={() => item.url && window.open(item.url, '_blank')}>
        <ItemMedia>
          {item.media_url ? (
            isImage ? (
              <Box
                component="img"
                src={item.media_url}
                alt={item.label || 'Item image'}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <video
                src={item.media_url}
                autoPlay
                loop
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )
          ) : (
            <Box
              sx={{
                width: '100%',
                height: 200,
                bgcolor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="overline">No Media</Typography>
            </Box>
          )}
        </ItemMedia>
        <Overlay className="itemOverlay">
          {item.label && <Typography variant="h6">{item.label}</Typography>}
          {item.description && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {item.description}
            </Typography>
          )}
        </Overlay>
      </ActionCard>
    );
  }

  const isHeaderImage = /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(headerMediaUrl);

  // 4) Final render
  return (
    <Box sx={{ p: 2 }}>
      <AlertDialog alertState={dialogState} />
      <Typography variant="h4" sx={{ mb: 2 }}>
        Manage {creatorId}'s Bio Pages
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {pages.map((p, i) => (
          <Button
            key={p.id}
            variant={selectedPageIndex === i ? 'contained' : 'outlined'}
            onClick={() => handleSelectPage(i)}
          >
            {p.slug}
          </Button>
        ))}
        <Button
          variant={selectedPageIndex === -1 ? 'contained' : 'outlined'}
          onClick={() => handleSelectPage(-1)}
        >
          + Create New
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* LEFT: Form */}
        <Paper sx={{ p: 2, minWidth: 300, flex: '1 1 300px' }} elevation={3}>
          <Typography variant="h6" gutterBottom>
            {isNewPage ? 'Create New Bio Page' : 'Edit Bio Page'}
          </Typography>
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            fullWidth
            margin="normal"
            disabled={!isNewPage}
          />
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            margin="normal"
          />
          <FormControlLabel
            control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
            label="Is Public?"
          />
          <FormControlLabel
            control={<Switch checked={qrEnabled} onChange={(e) => setQrEnabled(e.target.checked)} />}
            label="Enable QR Code"
          />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Header Media
          </Typography>
          <TextField
            label="Header Media URL"
            value={headerMediaUrl}
            onChange={(e) => setHeaderMediaUrl(e.target.value)}
            fullWidth
            margin="normal"
          />
          <Button variant="outlined" onClick={() => handleOpenUploadDialog('header')}>
            Upload Header Media
          </Button>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Theme Settings
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="outlined" onClick={() => setIsBgPickerOpen(true)}>
              Background: {themeObj.backgroundColor || '#FFFFFF'}
            </Button>
            <Button variant="outlined" onClick={() => setIsFgPickerOpen(true)}>
              Foreground: {themeObj.foregroundColor || '#000000'}
            </Button>
          </Box>
          <Button variant="contained" onClick={handleSave} sx={{ mt: 3 }}>
            Save
          </Button>
        </Paper>

        {/* RIGHT: Items & Preview */}
        <Box sx={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isMobile && (
            <Button variant="contained" onClick={() => setIsPreviewOpen(true)}>
              Show Preview
            </Button>
          )}

          <Paper sx={{ p: 2, flex: 1 }} elevation={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Items
            </Typography>
            <Button variant="outlined" onClick={handleAddItem} sx={{ mb: 1 }}>
              + Add Item
            </Button>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="itemsDroppable">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {items.map((it, idx) => (
                      <Draggable key={idx} draggableId={`item-${idx}`} index={idx}>
                        {(prov) => (
                          <Accordion ref={prov.innerRef} {...prov.draggableProps}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} {...prov.dragHandleProps}>
                              <Typography>
                                {it.label || '(Untitled)'} – {it.size}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField
                                  label="Label"
                                  size="small"
                                  value={it.label}
                                  onChange={(e) => handleUpdateItemField(idx, 'label', e.target.value)}
                                />
                                <TextField
                                  label="URL"
                                  size="small"
                                  value={it.url}
                                  onChange={(e) => handleUpdateItemField(idx, 'url', e.target.value)}
                                />
                                <TextField
                                  label="Description"
                                  size="small"
                                  multiline
                                  minRows={2}
                                  value={it.description}
                                  onChange={(e) =>
                                    handleUpdateItemField(idx, 'description', e.target.value)
                                  }
                                />
                                <TextField
                                  label="Media URL"
                                  size="small"
                                  value={it.media_url}
                                  onChange={(e) =>
                                    handleUpdateItemField(idx, 'media_url', e.target.value)
                                  }
                                />
                                <Button variant="outlined" onClick={() => handleOpenUploadDialog(idx)}>
                                  Upload Media
                                </Button>
                                <FormControl size="small" sx={{ width: 120, mt: 1 }}>
                                  <InputLabel>Size</InputLabel>
                                  <Select
                                    value={it.size}
                                    label="Size"
                                    onChange={(e) => handleUpdateItemField(idx, 'size', e.target.value)}
                                  >
                                    <MenuItem value="SMALL">SMALL</MenuItem>
                                    <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                                    <MenuItem value="LARGE">LARGE</MenuItem>
                                  </Select>
                                </FormControl>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <IconButton onClick={() => handleRemoveItem(idx)} sx={{ color: 'red' }}>
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Paper>

          {!isMobile && (
            <Paper sx={{ p: 2 }} elevation={1}>
              <Typography variant="h6" gutterBottom>
                Live Preview
              </Typography>
              <BioPageContainer customtheme={themeObj}>
                {headerMediaUrl && (
                  <Box sx={{ mb: 2 }}>
                    {isHeaderImage ? (
                      <Box
                        component="img"
                        src={headerMediaUrl}
                        alt="Header Media"
                        sx={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 1 }}
                      />
                    ) : (
                      <video src={headerMediaUrl} controls style={{ width: '100%', maxHeight: 300 }} />
                    )}
                  </Box>
                )}
                <Box sx={{ p: 2, width: '100%' }}>
                  <Typography variant="h4" textAlign="center" gutterBottom>
                    {title || slug || 'Untitled'}
                  </Typography>
                  <Typography variant="body1" textAlign="center">
                    {description}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2, width: '100%' }} />
                <Box sx={{ width: '100%', maxWidth: 600 }}>{items.map(renderPreviewItem)}</Box>
              </BioPageContainer>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Mobile Preview */}
      <Dialog fullScreen open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
        <Box sx={{ p: 2 }}>
          <Button onClick={() => setIsPreviewOpen(false)}>Close Preview</Button>
        </Box>
        <Box sx={{ p: 2 }}>
          <BioPageContainer customtheme={themeObj}>
            {headerMediaUrl && (
              <Box sx={{ mb: 2 }}>
                {isHeaderImage ? (
                  <Box
                    component="img"
                    src={headerMediaUrl}
                    alt="Header Media"
                    sx={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 1 }}
                  />
                ) : (
                  <video src={headerMediaUrl} controls style={{ width: '100%', maxHeight: 300 }} />
                )}
              </Box>
            )}
            <Box sx={{ p: 2, width: '100%' }}>
              <Typography variant="h4" textAlign="center" gutterBottom>
                {title || slug || 'Untitled'}
              </Typography>
              <Typography variant="body1" textAlign="center">
                {description}
              </Typography>
            </Box>
            <Divider sx={{ my: 2, width: '100%' }} />
            <Box sx={{ width: '100%', maxWidth: 600 }}>{items.map(renderPreviewItem)}</Box>
          </BioPageContainer>
        </Box>
      </Dialog>

      {/* Upload Dialog */}
      <UploadFileDialogPFP open={isUploadDialogOpen} onClose={handleCloseUploadDialog} />

      {/* Color Pickers */}
      <ColorPickerGrid
        open={isBgPickerOpen}
        onClose={() => setIsBgPickerOpen(false)}
        selectedColor={themeObj.backgroundColor}
        onColorSelect={(c) => setThemeObj((t) => ({ ...t, backgroundColor: c }))}
      />
      <ColorPickerGrid
        open={isFgPickerOpen}
        onClose={() => setIsFgPickerOpen(false)}
        selectedColor={themeObj.foregroundColor}
        onColorSelect={(c) => setThemeObj((t) => ({ ...t, foregroundColor: c }))}
      />
    </Box>
  );
}
